/**
 * Che cosa arriva davvero all'API quando si salva un modulo.
 *
 * Queste regole stavano dentro i componenti, dove erano vere ma non
 * verificabili: per provarle sarebbe servito un ambiente DOM, cioè una
 * dipendenza in più per controllare qualcosa che con il DOM non c'entra.
 *
 * E non sono presentazione. «Un campo vuoto diventa `null`, tranne il paese»,
 * «i decimali con la virgola diventano con il punto», «i conteggi si ricavano
 * dall'elenco»: sono affermazioni sul **contratto**, e ognuna nasce da un
 * rifiuto vero del server. Meritano di stare dove si possono leggere tutte
 * insieme, e di rompersi rumorosamente se qualcuno le cambia.
 */

/** I valori grezzi di un modulo, già estratti dal `FormData`. */
export type Campi = Readonly<Record<string, string>>

/**
 * I campi che il server **rifiuta** a `null`.
 *
 * Svuotandoli, la chiave si omette invece di azzerarla: `country` a `null`
 * risponde `400` con «The type of the "country" attribute must be "string"»,
 * che a chi sta compilando una scheda non dice niente. Omettendola resta il
 * valore precedente, e in creazione vale il predefinito del server.
 */
export const MAI_NULLI: ReadonlySet<string> = new Set(['country'])

/**
 * Trasforma i campi di testo in un corpo JSON.
 *
 * Vuoto vuol dire «non lo so», e sul server è `null`: mandare `""`
 * riempirebbe la colonna con una stringa vuota, che poi si comporta come un
 * dato presente — un indirizzo vuoto stampato su una lettera.
 */
export function corpoDaCampi(campi: Campi, chiavi: readonly string[]): Record<string, unknown> {
  const corpo: Record<string, unknown> = {}

  for (const chiave of chiavi) {
    const valore = (campi[chiave] ?? '').trim()

    if (valore === '' && MAI_NULLI.has(chiave)) continue

    corpo[chiave] = valore === '' ? null : valore
  }

  return corpo
}

/** I campi dell'anagrafica che il modulo delle persone manda sempre. */
export const CAMPI_PERSONA: readonly string[] = [
  'last_name',
  'first_name',
  'organization_name',
  'kennel_prefix',
  'kennel_prefix_date',
  'kennel_suffix',
  'qualification',
  'email',
  'mobile',
  'phone',
  'address',
  'postal_code',
  'city',
  'province',
  'country',
  'tax_code',
  'vat_number',
  'birth_date',
  'birth_place',
  'id_document_type',
  'id_document_number',
  'id_document_issuer',
  'id_document_issued_at',
  'id_document_expires_at',
  'notes',
]

/**
 * Il corpo per creare o modificare una persona.
 *
 * Il nome che non appartiene al tipo scelto si **azzera**, invece di restare
 * nascosto nel dato: una società con un cognome dentro è una riga che nessuno
 * capirà più fra due anni.
 */
export function corpoPersona(
  campi: Campi,
  tipo: 'individual' | 'organization',
  allevatore: boolean,
): Record<string, unknown> {
  const corpo = corpoDaCampi(campi, CAMPI_PERSONA)

  corpo['type'] = tipo
  corpo['breeder'] = allevatore

  if (tipo === 'organization') {
    corpo['last_name'] = null
    corpo['first_name'] = null
  } else {
    corpo['organization_name'] = null
  }

  return corpo
}

/** Un attributo come lo descrive il catalogo della specie. */
export interface AttributoSpecie {
  key: string
  type: string
}

/**
 * Gli attributi di specie, pronti da mandare.
 *
 * Due regole, entrambe nate da un rifiuto del server.
 *
 * **I decimali con la virgola diventano con il punto.** In Italia si scrive
 * «56,5», e il validatore usa `is_numeric()`, che la rifiuta: senza la
 * conversione chi digita come ha sempre digitato riceve «deve essere un
 * numero» davanti a un numero.
 *
 * **Gli attributi vuoti non si mandano.** Il server scarta i valori vuoti
 * prima di validare, ma mandare `""` per un attributo obbligatorio darebbe
 * «deve essere una stringa» invece di «è obbligatorio» — che è un'altra cosa,
 * e manda a cercare nel posto sbagliato.
 */
export function attributiSpecie(
  attributi: readonly AttributoSpecie[],
  valori: Campi,
): Record<string, unknown> {
  const risultato: Record<string, unknown> = {}

  for (const attributo of attributi) {
    const grezzo = valori[attributo.key]

    if (attributo.type === 'boolean') {
      risultato[attributo.key] = grezzo === 'on' || grezzo === 'true'

      continue
    }

    let testo = (grezzo ?? '').trim()

    if (attributo.type === 'decimal') testo = testo.replace(',', '.')

    if (testo !== '') risultato[attributo.key] = testo
  }

  return risultato
}

/** Un nato dichiarato in una denuncia di nascita. */
export interface Nato {
  name: string
  sex: 'M' | 'F' | 'U'
  color?: string
}

export type TipoDenuncia = 'registration' | 'mating' | 'birth' | 'transfer' | 'other'

/**
 * Il contenuto di una denuncia, che cambia col tipo.
 *
 * Si mandano **solo** le chiavi che quel tipo usa: una monta con dentro un
 * `new_owner_id` sarebbe un documento che dichiara qualcosa che non è stato
 * dichiarato. Le chiavi sono quelle che gli effetti del backend leggono
 * davvero — lette dal loro codice, non dedotte.
 */
export function contenutoDenuncia(
  tipo: TipoDenuncia,
  campi: Campi,
  riferimenti: { proprietarioId?: number | undefined; stalloneId?: number | undefined },
  nati: readonly Nato[] = [],
): Record<string, unknown> {
  const testo = (chiave: string) => (campi[chiave] ?? '').trim()
  const contenuto: Record<string, unknown> = {}

  if (tipo === 'registration') {
    contenuto['name'] = testo('name')
    contenuto['sex'] = testo('sex')
    if (testo('birth_date')) contenuto['birth_date'] = testo('birth_date')
    if (testo('color')) contenuto['color'] = testo('color')
    if (testo('breed_id')) contenuto['breed_id'] = Number(testo('breed_id'))
    if (riferimenti.proprietarioId) contenuto['owner_id'] = riferimenti.proprietarioId
  }

  if (tipo === 'mating') {
    if (riferimenti.stalloneId) contenuto['sire_id'] = riferimenti.stalloneId
    if (testo('mating_date')) contenuto['mating_date'] = testo('mating_date')
  }

  if (tipo === 'birth') {
    if (testo('birth_date')) contenuto['birth_date'] = testo('birth_date')

    const validi = nati.filter((nato) => nato.name.trim() !== '')

    contenuto['puppies'] = validi.map((nato) => ({
      name: nato.name.trim(),
      sex: nato.sex,
      ...(nato.color?.trim() ? { color: nato.color.trim() } : {}),
    }))

    // I conteggi si ricavano dall'elenco invece di chiederli a parte: due
    // campi che devono coincidere sono due occasioni di non farlo, e quando
    // non coincidono non dicono quale dei due sia giusto.
    contenuto['born_total'] = validi.length
    contenuto['born_male'] = validi.filter((n) => n.sex === 'M').length
    contenuto['born_female'] = validi.filter((n) => n.sex === 'F').length
  }

  if (tipo === 'transfer') {
    if (riferimenti.proprietarioId) contenuto['new_owner_id'] = riferimenti.proprietarioId
    if (testo('date')) contenuto['date'] = testo('date')
    if (testo('notes')) contenuto['notes'] = testo('notes')
  }

  if (tipo === 'other' && testo('notes')) contenuto['notes'] = testo('notes')

  return contenuto
}

/**
 * Un riferimento nella forma che l'API si aspetta.
 *
 * Le relazioni viaggiano come IRI, non come identificativi: un numero produce
 * un `400` che parla di tipi e non dice quale campo sia sbagliato.
 */
export function iri(risorsa: string, id: number | null | undefined): string | null {
  return typeof id === 'number' ? `/api/v1/${risorsa}/${String(id)}` : null
}
